import { useCallback, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { collectProofImageFiles } from "@/lib/proofImages";

const toneStyles = {
  emerald: {
    panel: "border-emerald-200 bg-emerald-50/70 text-emerald-950",
    active: "border-emerald-400 bg-emerald-100/70 ring-2 ring-emerald-300/40",
    icon: "text-emerald-600",
    chip: "border-emerald-200 bg-white/80 text-emerald-900",
  },
  amber: {
    panel: "border-amber-200 bg-amber-50/70 text-amber-950",
    active: "border-amber-400 bg-amber-100/70 ring-2 ring-amber-300/40",
    icon: "text-amber-600",
    chip: "border-amber-200 bg-white/80 text-amber-900",
  },
  neutral: {
    panel: "border-border bg-muted/20 text-foreground",
    active: "border-primary/40 bg-primary/5 ring-2 ring-primary/20",
    icon: "text-primary",
    chip: "border-border bg-background/80 text-foreground",
  },
};

const ProofImageDropzone = ({
  inputTestId,
  accept = "image/*",
  multiple = true,
  disabled = false,
  currentCount,
  maxCount,
  title = "Upload atau seret gambar ke sini",
  description = "atau klik untuk memilih file dari komputer",
  tone = "neutral",
  className,
  onFilesSelected,
}) => {
  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const styles = toneStyles[tone] || toneStyles.neutral;

  const clearDragState = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragging(false);
  }, []);

  const submitFiles = useCallback(
    async (files) => {
      if (disabled) {
        return;
      }

      const nextFiles = Array.from(files || []).filter(Boolean);
      if (nextFiles.length === 0) {
        return;
      }

      await Promise.resolve(onFilesSelected?.(nextFiles));
    },
    [disabled, onFilesSelected]
  );

  const openPicker = useCallback(() => {
    if (disabled) {
      return;
    }

    inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (event) => {
      const nextFiles = collectProofImageFiles(event.target.files);
      event.target.value = "";
      void submitFiles(nextFiles);
    },
    [submitFiles]
  );

  const handleDragEnter = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setIsDragging(false);
      }
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearDragState();
      const nextFiles = collectProofImageFiles(event.dataTransfer);
      if (nextFiles.length === 0) {
        toast.error("Tidak ada gambar yang terbaca dari drag ini. Coba drag ulang atau klik untuk pilih file.");
        return;
      }
      void submitFiles(nextFiles);
    },
    [clearDragState, disabled, submitFiles]
  );

  const handlePaste = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      const nextFiles = collectProofImageFiles(event.clipboardData);
      if (nextFiles.length === 0) {
        return;
      }

      event.preventDefault();
      void submitFiles(nextFiles);
    },
    [disabled, submitFiles]
  );

  return (
    <div className={cn("space-y-2", className)}>
      <input
        ref={inputRef}
        data-testid={inputTestId}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        onChange={handleInputChange}
        disabled={disabled}
      />

      <button
        type="button"
        className={cn(
          "group flex w-full cursor-pointer flex-col gap-4 rounded-2xl border border-dashed px-4 py-5 text-left shadow-sm transition hover:shadow-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          disabled && "cursor-not-allowed opacity-60",
          isDragging ? styles.active : styles.panel,
        )}
        onClick={openPicker}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
        disabled={disabled}
        aria-label={title}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-white/80 shadow-sm", styles.icon)}>
              <Upload className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <p className="font-semibold leading-tight">{title}</p>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            {typeof currentCount === "number" && typeof maxCount === "number" ? (
              <span className={cn("rounded-full border px-3 py-1 text-xs font-medium", styles.chip)}>
                {currentCount}/{maxCount} foto
              </span>
            ) : null}
            <span className={cn("rounded-full border px-3 py-1 text-xs font-medium", styles.chip)}>
              Klik untuk pilih file
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};

export default ProofImageDropzone;
